import { useState, useRef } from "react";
import { X, GripHorizontal } from "lucide-react";

const NOTE_COLORS = [
  { bg: "#fef08a", border: "#eab308", label: "Yellow" },
  { bg: "#fed7aa", border: "#f97316", label: "Orange" },
  { bg: "#bbf7d0", border: "#22c55e", label: "Green" },
  { bg: "#bfdbfe", border: "#3b82f6", label: "Blue" },
  { bg: "#f5d0fe", border: "#a855f7", label: "Purple" },
  { bg: "#fecaca", border: "#ef4444", label: "Red" },
];

export function StickyNote({ note, onUpdate, onDelete, readonly = false }) {
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(note.text || "");
  const [dragging, setDragging] = useState(false);
  const dragOffset = useRef({ x: 0, y: 0 });
  const colorConfig = NOTE_COLORS.find(c => c.bg === note.color) || NOTE_COLORS[0];

  const handleMouseDown = (e) => {
    if (readonly || e.target.tagName === "TEXTAREA" || e.target.closest("button")) return;
    e.preventDefault();
    setDragging(true);
    dragOffset.current = { x: e.clientX - note.x, y: e.clientY - note.y };

    const onMove = (me) => {
      onUpdate?.({ ...note, x: me.clientX - dragOffset.current.x, y: me.clientY - dragOffset.current.y });
    };
    const onUp = () => {
      setDragging(false);
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  };

  const handleTextBlur = () => {
    setEditing(false);
    onUpdate?.({ ...note, text });
  };

  const cycleColor = () => {
    const idx = NOTE_COLORS.findIndex(c => c.bg === note.color);
    const next = NOTE_COLORS[(idx + 1) % NOTE_COLORS.length];
    onUpdate?.({ ...note, color: next.bg });
  };

  return (
    <div
      data-testid={`sticky-note-${note.id}`}
      style={{
        position: "absolute",
        left: note.x,
        top: note.y,
        width: 180,
        minHeight: 120,
        background: colorConfig.bg,
        border: `2px solid ${colorConfig.border}`,
        borderRadius: 8,
        boxShadow: "3px 4px 10px rgba(0,0,0,0.15)",
        zIndex: 100,
        cursor: dragging ? "grabbing" : "grab",
        userSelect: "none",
        transition: dragging ? "none" : "box-shadow 0.15s",
      }}
      onMouseDown={handleMouseDown}
    >
      {/* Header bar */}
      <div
        style={{ background: colorConfig.border, borderRadius: "6px 6px 0 0", height: 28, display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0 6px" }}
      >
        <GripHorizontal size={14} color="white" style={{ opacity: 0.8 }} />
        <div style={{ display: "flex", gap: 4 }}>
          {!readonly && (
            <button
              onClick={cycleColor}
              style={{ width: 14, height: 14, borderRadius: "50%", background: "white", border: "none", cursor: "pointer", opacity: 0.8 }}
              title="Change color"
            />
          )}
          {!readonly && (
            <button
              onClick={() => onDelete?.(note.id)}
              data-testid={`delete-note-${note.id}`}
              style={{ background: "none", border: "none", cursor: "pointer", color: "white", padding: 0, display: "flex", alignItems: "center" }}
            >
              <X size={14} />
            </button>
          )}
        </div>
      </div>

      {/* Note body */}
      <div style={{ padding: "8px 10px" }}>
        {editing && !readonly ? (
          <textarea
            autoFocus
            value={text}
            onChange={e => setText(e.target.value)}
            onBlur={handleTextBlur}
            style={{
              width: "100%", minHeight: 80, resize: "vertical", background: "transparent",
              border: "none", outline: "none", fontSize: 13, fontFamily: "Manrope, sans-serif",
              color: "#334155", lineHeight: 1.5,
            }}
          />
        ) : (
          <div
            onDoubleClick={() => !readonly && setEditing(true)}
            style={{ minHeight: 80, fontSize: 13, fontFamily: "Manrope, sans-serif", color: "#334155", lineHeight: 1.5, whiteSpace: "pre-wrap", wordBreak: "break-word" }}
          >
            {note.text || <span style={{ opacity: 0.5, fontStyle: "italic" }}>Double-click to edit...</span>}
          </div>
        )}
      </div>
    </div>
  );
}
